import { Heading } from '@efdigital/ll-addon-ui';
import type { CoachingCoachSessionsModel } from '@efdigital/ll-xceed-v2/routes/coaching/coach/sessions';
import { PAGE_WRAPPER } from '@efdigital/ll-xceed-v2/utils/layout';
import { LinkTo } from '@ember/routing';
import type RouterService from '@ember/routing/router-service';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { pageTitle } from 'ember-page-title';

interface CoachingCoachSessionsSignature {
  Args: {
    model: CoachingCoachSessionsModel;
    controller: unknown;
  };
}

export default class CoachingCoachSessions extends Component<CoachingCoachSessionsSignature> {
  @service declare router: RouterService;

  ariaCurrent = (routeName: string): 'page' | undefined =>
    this.router.currentRouteName === routeName ? 'page' : undefined;

  get actionRequiredCountBadgeClass(): string {
    const active =
      this.router.currentRouteName ===
      'coaching.coach.sessions.action-required';

    return active
      ? 'badge rounded-pill bg-body text-primary ms-1 fw-semibold'
      : 'badge rounded-pill text-bg-warning ms-1 fw-semibold';
  }

  get viewDescription(): string {
    switch (this.router.currentRouteName) {
      case 'coaching.coach.sessions.action-required':
        return 'Bookings to confirm and sessions that need your attention.';
      case 'coaching.coach.sessions.past':
        return 'Sessions you have delivered, completed, or canceled.';
      default:
        return 'Scheduled sessions coming up on your calendar.';
    }
  }

  <template>
    {{pageTitle "Sessions"}}

    <div class={{PAGE_WRAPPER}}>
      <Heading
        @size="lg"
        @text="Sessions"
        @secondary={{this.viewDescription}}
        @class="mb-4"
      />

      {{! Route links, not ARIA tabs: each view has its own URL. }}
      <nav class="py-2 mb-4 overflow-x-auto" aria-label="Session views">
        <ul
          class="nav nav-pills ll-bg-raised rounded-pill p-1 shadow-sm d-inline-flex flex-nowrap mb-0"
        >
          <li class="nav-item">
            <LinkTo
              @route="coaching.coach.sessions.index"
              class="nav-link rounded-pill px-3 px-md-4 fw-semibold"
              aria-current={{this.ariaCurrent "coaching.coach.sessions.index"}}
            >Upcoming</LinkTo>
          </li>
          <li class="nav-item">
            <LinkTo
              @route="coaching.coach.sessions.action-required"
              class="nav-link rounded-pill px-3 px-md-4 fw-semibold"
              aria-current={{this.ariaCurrent
                "coaching.coach.sessions.action-required"
              }}
            >Action required
              {{#if @model.actionRequiredCount}}
                <span class={{this.actionRequiredCountBadgeClass}}>
                  {{@model.actionRequiredCount}}
                  <span class="visually-hidden">
                    sessions need attention</span>
                </span>
              {{/if}}
            </LinkTo>
          </li>
          <li class="nav-item">
            <LinkTo
              @route="coaching.coach.sessions.past"
              class="nav-link rounded-pill px-3 px-md-4 fw-semibold"
              aria-current={{this.ariaCurrent "coaching.coach.sessions.past"}}
            >History</LinkTo>
          </li>
        </ul>
      </nav>

      {{outlet}}
    </div>
  </template>
}
